import React, { useEffect, useMemo, useState } from "react";
import { useUserContext } from "../../../../contexts/UserContext";
import useComponentVisible from "../../../../components/UI/ComponentVisible";
import { formatDate } from "../../../../utils/converter";
import { Search } from "lucide-react";
import { PrimaryButton, AgTableCard, Input } from "../../../../components/UI";
import AddUpdateWorkspaceModal from "../../../../components/Workspaces/AddUpdateWorkspaceModal";
import {
  deleteWorkspace,
  updateWorkspace,
} from "../../../../utils/APIs";
import dotsImgUrl from "../../../../assets/three-dots-vertical.svg";
import Loader from "../../../../components/UI/Loader/Loader";
import { toast } from 'react-toastify';
import DeleteWorkspaceModal from "../../../../components/Workspaces/DeleteWorkspaceModal";
import { permission } from "../../../../config/permission";
import { useContentfulContext } from "../../../../contexts/ContentfulContext";
import { getAgGridSortStorageKey } from "../../../../components/UI/AgGrid";

const ManageWorkspaces = () => {
  const { userData, checkPermission, updateWorkspaces } = useUserContext();
  const { content } = useContentfulContext();

  const [workspaceData, setWorkspaceData] = useState([]);
  const [loading] = useState(false);
  const [openAddUpdateWorkspaceModal, setOpenAddUpdateWorkspaceModal] =
    useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState("");
  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] =
    useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const [gridApi, setGridApi] = useState(null);

  const colDefs = [
    {
      headerName:
        content.WORKSPACE_MANAGEMENT_CONTENT.workspace_table_headers.workspace_id,
      field: "workspace_id",
      cellRenderer: ({ data }) => (
        <button
          className="text-center block cursor-pointer text-primary"
          onClick={() => handleOpenEdit(data)}
        >
          {data.workspace_id}
        </button>
      ),
      resizable: false,
      suppressMovable: true,
    },
    {
      headerName:
        content.WORKSPACE_MANAGEMENT_CONTENT.workspace_table_headers.workspace_name,
      field: "workspace_name",
      cellRenderer: ({ data }) => (
        <button
          className="text-center block cursor-pointer text-primary"
          onClick={() => handleOpenEdit(data)}
        >
          {data.workspace_name}
        </button>
      ),
      filter: true,
      sortable: true,
      suppressMovable: true,
      resizable: true,
    },
    {
      headerName: content.WORKSPACE_MANAGEMENT_CONTENT.workspace_table_headers.source,
      field: "source",
      cellRenderer: ({ data }) => {
        if(data.source == 'adobe_target') {
          return 'Adobe Target';
        } else if(data.source == 'stride') {
          return 'Stride';
        }
        return '';
      },
      sortable: false,
      cellDataType: "string",
      resizable: false,
      suppressMovable: true,
    },
    {
      headerName: content.WORKSPACE_MANAGEMENT_CONTENT.workspace_table_headers.default,
      field: "default",
      sortable: false,
      cellDataType: "string",
      resizable: false,
      suppressMovable: true,
    },
    {
      headerName:
        content.WORKSPACE_MANAGEMENT_CONTENT.workspace_table_headers.last_edited_by,
      field: "last_edited_by",
      cellRenderer: ({ data }) => data.last_edited_by,
      sortable: false,
      resizable: false,
      suppressMovable: true,
    },
    {
      headerName:
        content.WORKSPACE_MANAGEMENT_CONTENT.workspace_table_headers.updatedAt,
      field: "updatedAt",
      cellRenderer: ({ data }) => formatDate(data.updatedAt),
      comparator: (date1, date2) => new Date(date1) - new Date(date2),
      suppressMovable: true,
      resizable: false,
      sortable: true,
    },
    {
      headerName: "",
      field: "actions",
      cellRenderer: ({ data }) => {
        return (
          <div className="relative inline-block text-left">
            <button
              className="text-center block cursor-pointer actions"
              onClick={(e) => {
                setIsComponentVisible(!isComponentVisible);
                setSelectedItem(data);
                const buttonRect = e.target.getBoundingClientRect();
                setMenuPosition({
                  top: buttonRect.bottom,
                  left: buttonRect.left,
                });
              }}
            >
              <img src={dotsImgUrl} alt="" className="w-4 h-auto" />
            </button>
          </div>
        );
      },
      width: 50,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
    },
  ];

  const onFilterTextBoxChanged = (event) => {
    const filterValue = event.target.value.toLowerCase();
    gridApi.setFilterModel({
      workspace_id: { type: "contains", filter: filterValue },
      workspace_name: { type: "contains", filter: filterValue },
    });
  };

  const onGridReady = (params) => {
    setGridApi(params.api);

    const storageKey = getAgGridSortStorageKey("manage-workspaces-table");
    const sortModel = JSON.parse(localStorage.getItem(storageKey));

    if (!sortModel || sortModel?.length === 0) {
      params.columnApi.applyColumnState({
        state: [
          { colId: 'updatedAt', sort: 'desc' }
        ],
        applyOrder: false,
      });
    }
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitGridWidth",
      defaultMinWidth: 40,
      columnLimits: [
        { colId: "workspace_id", minWidth: 150 },
        { colId: "workspace_name", minWidth: 300 },
        { colId: "source", minWidth: 150 },
        { colId: "default", minWidth: 100 },
        { colId: "last_edited_by", minWidth: 200 },
        { colId: "updatedAt", minWidth: 200 },
        { colId: "actions", minWidth: 50 },
      ],
    };
  }, []);

  useEffect(() => {
    setWorkspaceData(userData?.workspaces);
  }, [userData?.workspaces]);

  // const getAllWorkspaceData = async () => {
  //   try {
  //     setLoading(true);
  //     const result = await getNewWorkspaceData(
  //       userData.token,
  //       userData.selectedOrganization.org_token,
  //       userData?.defaultAdobeIO.tenant_name
  //     );
  //     setWorkspaceData(result.data);
  //   } catch (error) {
  //     toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleCloseModal = (msg) => {
    setSelectedItem(null);
    setOpenAddUpdateWorkspaceModal(false);
    toast.success(msg);
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await deleteWorkspace(
        userData.token,
        userData.selectedOrganization.org_token,
        selectedItem._id
      );
      toast.success(content.WORKSPACE_MANAGEMENT_CONTENT.success.delete);
      const updateDefault = (userData?.defaultWorkspace?._id == selectedItem._id) ? true : false;
      updateWorkspaces(updateDefault);
      setSelectedItem(null);
      setOpenDeleteConfirmationModal(false);
    } catch (error) {
      toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetAsDefault = async () => {
    try {
      setIsComponentVisible(!isComponentVisible);
      setActionLoading(true);
      await updateWorkspace(
        userData.token,
        userData.selectedOrganization.org_token,
        selectedItem._id,
        { default: true }
      );
      updateWorkspaces();
      setSelectedItem(null);
      toast.success(content.WORKSPACE_MANAGEMENT_CONTENT.success.edit);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenEdit = (data) => {
    setSelectedItem(data);
    setOpenAddUpdateWorkspaceModal(true);
  };

  return (
    <div className="w-full px-4 md:px-8 my-6">
      <main id="main" className="flex flex-col space-y-3 items-start flex-grow">
        <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">
          {content.WORKSPACE_MANAGEMENT_CONTENT.title}
        </h1>
        <div className="flex flex-col pb-6 w-full">
          <p className="mb-4">{content.WORKSPACE_MANAGEMENT_CONTENT.description}</p>
          <AgTableCard
            header={
              <>
                <div className="w-full sm:w-80">
                  <Input
                    placeholder={content.WORKSPACE_MANAGEMENT_CONTENT.search_placeholder}
                    onChange={onFilterTextBoxChanged}
                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                    clearable
                  />
                </div>
                <PrimaryButton
                  handleClick={() => {
                    setSelectedItem(null);
                    setOpenAddUpdateWorkspaceModal(true);
                  }}
                >
                  {content.WORKSPACE_MANAGEMENT_CONTENT.add_btn}
                </PrimaryButton>
              </>
            }
            tableId="manage-workspaces-table"
            loading={loading}
            rowData={workspaceData}
            columnDefs={colDefs}
            autoSizeStrategy={autoSizeStrategy}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={
              content.WORKSPACE_MANAGEMENT_CONTENT.default_page_size
            }
          >
            {isComponentVisible && (
              <div
                ref={ref}
                className="absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                style={{ top: menuPosition.top, left: menuPosition.left - 192 }}
              >
                <div className="py-1">
                  <p
                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                    tabindex="-1"
                    onClick={() => setOpenAddUpdateWorkspaceModal(true)}
                  >
                    {content.WORKSPACE_MANAGEMENT_CONTENT.more_info_options.edit}
                  </p>
                  { selectedItem?.source === 'stride' && <p
                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                    tabindex="-1"
                    onClick={() => setOpenDeleteConfirmationModal(true)}
                  >
                    {content.WORKSPACE_MANAGEMENT_CONTENT.more_info_options.delete}
                  </p> }
                  {checkPermission(permission.EDIT_TEALIUM_OP_EDIT_DEFAULT) && <p
                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                    tabindex="-1"
                    onClick={handleSetAsDefault}
                  >
                    {content.WORKSPACE_MANAGEMENT_CONTENT.more_info_options.set_default}
                  </p>}
                </div>
              </div>
            )}
          </AgTableCard>
        </div>
      </main>
      {openAddUpdateWorkspaceModal && (
        <AddUpdateWorkspaceModal
          workspaceData={selectedItem}
          handleClose={handleCloseModal}
          setOpenAddUpdateWorkspaceModal={setOpenAddUpdateWorkspaceModal}
        />
      )}
      {openDeleteConfirmationModal && (
        <DeleteWorkspaceModal 
          hideModal={setOpenDeleteConfirmationModal}
          handleConfirmation={handleDelete} 
          loading={actionLoading}
        />
      )}
      {actionLoading && <Loader />}
    </div>
  );
};

export default ManageWorkspaces;
